import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Keyboard } from "lucide-react";

interface ShortcutGroup {
  title: string;
  context: string;
  shortcuts: { keys: string[]; label: string }[];
}

const SHORTCUT_GROUPS: ShortcutGroup[] = [
  {
    title: "Global",
    context: "Available everywhere",
    shortcuts: [
      { keys: ["?"], label: "Open this cheat sheet" },
    ],
  },
  {
    title: "Command Center — Bloomberg Mode",
    context: "/command-center",
    shortcuts: [
      { keys: ["1", "2", "3", "4"], label: "Switch to tab by number" },
      { keys: ["←", ""], label: "Cycle tabs left / right" },
      { keys: ["F"], label: "Toggle fullscreen terminal" },
      { keys: ["Esc"], label: "Exit fullscreen" },
    ],
  },
  {
    title: "Terminal Map",
    context: "Command Center  Global Map",
    shortcuts: [
      { keys: ["S"], label: "Toggle sidebar panel" },
    ],
  },
  {
    title: "Trade Journal",
    context: "/journal",
    shortcuts: [
      { keys: ["N"], label: "Log new trade" },
      { keys: ["/"], label: "Focus search" },
      { keys: ["J", ""], label: "Select next trade" },
      { keys: ["K", ""], label: "Select previous trade" },
      { keys: ["Enter"], label: "Expand / collapse selected trade" },
      { keys: ["E"], label: "Edit selected trade" },
      { keys: ["Esc"], label: "Deselect & collapse" },
    ],
  },
  {
    title: "Execution Mode",
    context: "/execution",
    shortcuts: [
      { keys: ["B"], label: "Buy" },
      { keys: ["S"], label: "Sell" },
      { keys: ["C"], label: "Close all positions" },
      { keys: ["Esc"], label: "Cancel" },
    ],
  },
];

export function openShortcutsModal() {
  window.dispatchEvent(new CustomEvent("open-shortcuts-modal"));
}

export function KeyboardShortcutsModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const handleCustom = () => setOpen(true);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("open-shortcuts-modal", handleCustom);
    return () => {
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("open-shortcuts-modal", handleCustom);
    };
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-primary" />
            Keyboard Shortcuts
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="flex items-baseline gap-2 mb-2">
                <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
                <span className="text-[10px] text-muted-foreground font-mono">{group.context}</span>
              </div>
              <div className="space-y-1.5">
                {group.shortcuts.map((s) => (
                  <div key={s.label} className="flex items-center justify-between py-1.5 px-3 rounded-lg bg-muted/40">
                    <span className="text-sm text-foreground">{s.label}</span>
                    <div className="flex items-center gap-1">
                      {s.keys.map((k) => (
                        <kbd
                          key={k}
                          className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded border border-border bg-background text-xs font-mono font-medium text-muted-foreground shadow-sm"
                        >
                          {k}
                        </kbd>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-[10px] text-muted-foreground text-center mt-4 pt-3 border-t border-border">
          Press <kbd className="px-1 py-0.5 rounded border border-border bg-background text-[10px] font-mono">?</kbd> anytime to toggle this panel
        </p>
      </DialogContent>
    </Dialog>
  );
}
